import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { EMPTY, catchError, map, mergeMap, of, switchMap, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { SnackbarService } from "../../core/services";
import { CalendarApi } from "./calendar.api";
import * as CalendarActions from "./calendar.actions";
import { selectCalendarRange } from "./calendar.selectors";

@Injectable()
export class CalendarEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(CalendarApi);
  private readonly snackbar = inject(SnackbarService);
  private readonly store = inject(Store);

  fetchEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventsFetchRequest),
      switchMap(({ start, end }) =>
        this.api.listEvents(start, end).pipe(
          map((response) => CalendarActions.CalendarEventsFetchSuccess({ start, end, events: response.data })),
          catchError((error) => of(CalendarActions.CalendarEventsFetchFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  createEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventCreateRequest),
      switchMap(({ payload, attachMeetrixLink }) =>
        this.api.createEvent(payload).pipe(
          mergeMap((response) => {
            const created = response.data;
            const actions: any[] = [CalendarActions.CalendarEventCreateSuccess({ event: created })];
            if (attachMeetrixLink && created?.id) {
              // actions.push(CalendarActions.CalendarEventAttachMeetrixLinkRequest({ eventId: created.id }));
            }
            return actions;
          }),
          catchError((error) => of(CalendarActions.CalendarEventCreateFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  createEventSuccessRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventCreateSuccess),
      withLatestFrom(this.store.select(selectCalendarRange)),
      switchMap(([, range]) => (range ? of(CalendarActions.CalendarEventsFetchRequest({ start: range.start, end: range.end })) : EMPTY)),
    ),
  );

  updateEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventUpdateRequest),
      switchMap(({ eventId, payload, attachMeetrixLink }) =>
        this.api.updateEvent(eventId, payload).pipe(
          mergeMap((response) => {
            const updated = response.data;
            const actions: any[] = [CalendarActions.CalendarEventUpdateSuccess({ event: updated })];
            // Do not auto-attach a meeting link on update.
            // Online meeting rooms are created on-demand when a participant joins via their unique token link.
            // This avoids extra API calls and prevents participant token duplication caused by mixed flows.
            void attachMeetrixLink;
            return actions;
          }),
          catchError((error) => of(CalendarActions.CalendarEventUpdateFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  deleteEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventDeleteRequest),
      switchMap(({ eventId }) =>
        this.api.deleteEvent(eventId).pipe(
          map(() => CalendarActions.CalendarEventDeleteSuccess({ eventId })),
          catchError((error) => of(CalendarActions.CalendarEventDeleteFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  failureNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CalendarActions.CalendarEventsFetchFailure,
          CalendarActions.CalendarEventCreateFailure,
          CalendarActions.CalendarEventUpdateFailure,
          CalendarActions.CalendarEventDeleteFailure,
        ),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Calendar error: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );

  joinEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventJoinRequest),
      switchMap(({ eventId }) =>
        this.api.joinMeeting(eventId).pipe(
          map((response) => {
            return CalendarActions.CalendarEventJoinSuccess({ payload: response.data });
          }),
          catchError((error) => of(CalendarActions.CalendarEventJoinFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  joinEventFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CalendarActions.CalendarEventJoinFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Unable to join meeting: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );

  externalJoin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarEventExternalLinkRequest),
      switchMap(({ token }) =>
        this.api.joinMeetrixByToken(token).pipe(
          map((response) => CalendarActions.CalendarEventExternalLinkSuccess({ payload: response.data })),
          catchError((error) => of(CalendarActions.CalendarEventExternalLinkFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  externalJoinFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CalendarActions.CalendarEventExternalLinkFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Unable to join meeting: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );

  inviteParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarActions.CalendarInviteParticipantRequest),
      switchMap(({ eventId, email, name }) =>
        this.api.inviteParticipant(eventId, { email, name }).pipe(
          map((response) => CalendarActions.CalendarInviteParticipantSuccess({ payload : response.data })),
          catchError((error) => of(CalendarActions.CalendarInviteParticipantFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  inviteParticipantFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CalendarActions.CalendarInviteParticipantFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Unable to send invite: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );


  
}
